import ToggleField from 'components/ToggleField';
import LinkToIcon from 'icons/LinkTo.icon';
import SparklesIcon from 'icons/Sparkles.icon';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useCallbackRef from 'hooks/useCallbackRef';
import RadioGroupField from 'components/RadioGroupField';
import { useGenerateSliderOptions } from './hooks';
import TextAreaField from 'components/TextAreaField';
import DropzoneField from 'components/DropzoneField';
import api from 'api';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  INCLUDE_VIDEO_IN_TEASER,
  Listing,
  TEASER_TYPE,
  TeaserType,
  TeaserVerification,
} from 'models/Listing';
import validations from 'validations';
import { getCreateListingRoute } from 'utils';
import useHeightAnimation from 'hooks/useHeightAnimation';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';
import { FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';

import './EnableTeaser.styles.scss';
import './EnableTeaser.styles.responsive.scss';

const SUPPORTED_IMAGE_FILE_TYPES = [
  FILE_TYPES.PDF,
  FILE_TYPES.IMAGE_JPEG,
  FILE_TYPES.IMAGE_PNG,
];

const SUPPORTED_VIDEO_FILE_TYPES = [FILE_TYPES.VIDEOS];

type EnableTeaserProps = {
  listing?: Partial<Listing>;
  isEditing?: boolean;
};

const EnableTeaser: FC<EnableTeaserProps> = ({ listing, isEditing }) => {
  const { t } = useTranslation();
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();
  const { popServerError } = useContext(PopNotificationsContext);
  const { patchListingValue } = useContext(PatchListingContext);

  const {
    values: {
      enableTeaser,
      teaserType,
      includeVideoInTeaser,
      revenue,
      COGS,
      EBIT,
      EBITDA,
      lessOverheads,
      depreciationAndAmortisation,
      accountingPeriod,
    },
  } = useFormState();

  const { dealRoomId, type, _id: listingId } = listing;

  const isDisabledTeaser =
    !revenue ||
    !COGS ||
    !EBIT ||
    !EBITDA ||
    !lessOverheads ||
    !depreciationAndAmortisation ||
    !accountingPeriod;

  useHeightAnimation({
    element: container,
  });

  const uploadListingFile: DropzoneUpload = useCallback(
    async (file, fileName, config, customName) => {
      try {
        const { uploadedFile, listing } = await api.listings.uploadFile(
          file,
          fileName,
          listingId,
          config,
          customName,
        );

        return { ...uploadedFile, id: listing?.teaserFile?._id };
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popServerError],
  );

  const deleteListingFile: DropzoneDelete = useCallback(
    async (file, fileName) => {
      try {
        const {
          data: { fileId },
        } = await api.listings.deleteFile(listingId, fileName, file.id);

        return fileId;
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popServerError],
  );

  const uploadListingVideo: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      try {
        await api.listings.uploadListingFileToAws(
          listingId,
          file,
          fileName,
          config,
        );

        const { data } = await api.listings.getTeaserVideo(listingId);

        return { ...data, name: file.name };
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popServerError],
  );

  const previewTeaserLink = useMemo(
    () =>
      getCreateListingRoute(dealRoomId, listingId, type, isEditing) +
      '/default/teaser-preview',
    [dealRoomId, isEditing, listingId, type],
  );

  const { teaserTypeOptions, verifyBuyersOptions, includeVideoOptions } =
    useGenerateSliderOptions(previewTeaserLink);

  if (isDisabledTeaser) return null;

  return (
    <div className="lcw-enable-teaser" ref={containerRef}>
      <div className="lcw-enable-teaser__select">
        <SparklesIcon />

        <div>
          <p>{t('activateDigitalTeaser')}</p>
          <p className="recommended">{t('leadsWhoReceiveInfo')}</p>

          <Link className="lcw-enable-teaser__link" to={previewTeaserLink}>
            {t('previewTeaser')}
            <LinkToIcon />
          </Link>
        </div>

        <Field
          name="enableTeaser"
          type="checkbox"
          className="black-toggle"
          component={ToggleField}
          customOnChange={(enableTeaser: boolean) =>
            patchListingValue({ enableTeaser })
          }
        />
      </div>

      {enableTeaser && (
        <div className="lcw-enable-teaser__enabled">
          <div>
            <p className="lcw-enable-teaser__title">
              {t('verifyBuyerLeadsBeforeTeaser')}
            </p>
            <RadioGroupField
              name="teaserVerification"
              options={verifyBuyersOptions}
              customOnChange={(teaserVerification: TeaserVerification) => {
                patchListingValue({ teaserVerification });
              }}
            />
          </div>

          <div>
            <p className="lcw-enable-teaser__title">
              {t('confirmWhichTeaserToShare')}
            </p>
            <RadioGroupField
              name="teaserType"
              options={teaserTypeOptions}
              customOnChange={(teaserType: TeaserType) => {
                patchListingValue({ teaserType });
              }}
            />
            {teaserType === TEASER_TYPE.OWN_TEASER_FILE && (
              <Field
                accept={SUPPORTED_IMAGE_FILE_TYPES}
                component={DropzoneField}
                maxFiles={1}
                dropMessage={t('pdf,jpeg,png')}
                showApplicationAsIframe
                className="lcw-enable-teaser__upload"
                name="teaserFile"
                validate={validations.required()}
                uploadRequest={uploadListingFile}
                deleteRequest={deleteListingFile}
              />
            )}
          </div>

          {teaserType !== TEASER_TYPE.OWN_TEASER_FILE && (
            <div>
              <p className="lcw-enable-teaser__title">
                {t('includeVideoInTeaser')}
              </p>

              <RadioGroupField
                name="includeVideoInTeaser"
                options={includeVideoOptions}
                customOnChange={(includeVideoInTeaser) => {
                  patchListingValue({
                    includeVideoInTeaser,
                  });
                }}
              />
              {includeVideoInTeaser ===
                INCLUDE_VIDEO_IN_TEASER.WITH_EMBEDDED_CODE && (
                <Field
                  component={TextAreaField}
                  validate={validations.required()}
                  label={t('pasteEmbeddedVideo')}
                  name="embeddedVideoCode"
                  customOnBlur={(
                    e: React.FocusEvent<HTMLInputElement>,
                    isValid: boolean,
                  ) => {
                    if (isValid) {
                      patchListingValue({
                        embeddedVideoCode: e.target.value,
                      });
                    }
                  }}
                />
              )}
              {includeVideoInTeaser ===
                INCLUDE_VIDEO_IN_TEASER.WITH_VIDEO_FILE && (
                <Field
                  accept={SUPPORTED_VIDEO_FILE_TYPES}
                  component={DropzoneField}
                  maxFiles={1}
                  dropMessage={t('mp4,avi,mov')}
                  showApplicationAsIframe
                  className="lcw-enable-teaser__upload"
                  name="embeddedTeaserVideoFile"
                  validate={validations.required()}
                  uploadRequest={uploadListingVideo}
                  deleteRequest={deleteListingFile}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnableTeaser;
